import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../shared/lib/supabase';
import type { N8nInstance } from '../../../shared/types';

/**
 * Hook para listar todas as instâncias N8N
 * @param enabledOnly - Se true, retorna apenas instâncias habilitadas
 */
export function useN8nInstances(enabledOnly = false) {
  return useQuery({
    queryKey: ['n8n-instances', enabledOnly],
    queryFn: async () => {
      let query = supabase
        .from('n8n_instances')
        .select('*');
      
      if (enabledOnly) {
        query = query.eq('is_active', true);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data as N8nInstance[];
    },
  });
}

/**
 * Hook para criar uma nova instância N8N
 */
export function useCreateN8nInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (instance: Omit<N8nInstance, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('n8n_instances')
        .insert([instance])
        .select()
        .single();

      if (error) throw error;
      return data as N8nInstance;
    },
    onSuccess: () => {
      // Invalida a query para refazer o fetch
      queryClient.invalidateQueries({ queryKey: ['n8n-instances'] });
    },
  });
}

/**
 * Hook para atualizar uma instância N8N existente
 */
export function useUpdateN8nInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<N8nInstance> & { id: string }) => {
      const { data, error } = await supabase
        .from('n8n_instances')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as N8nInstance;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['n8n-instances'] });
    },
  });
}

/**
 * Hook para deletar uma instância N8N
 */
export function useDeleteN8nInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('n8n_instances')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['n8n-instances'] });
    },
  });
}
